<script setup lang="ts">
import { computed, ref, inject, watch, onMounted } from 'vue';
import type { Ref } from 'vue';
import { DtItemLayout } from '@dialpad/dialtone/vue3/lib/item-layout';
import { DtIllustration } from '@dialpad/dialtone/vue3/lib/illustration';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { DtBadge } from '@dialpad/dialtone/vue3/lib/badge';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtValidationMessages } from '@dialpad/dialtone/vue3/lib/validation-messages';
import { DtLink } from '@dialpad/dialtone/vue3/lib/link';
import { DtTooltip } from '@dialpad/dialtone/vue3/lib/tooltip';
import { validateFile, validateHeaders } from '@/csv/utils/csv-file-parser';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import type { CsvPostAssertErrorResponseTypeEnum } from '@/apis/generated/dialbunny';
import { useI18N } from '@dialpad/i18n';
import { DATA_QA, TEMPLATE_DATA } from './csv-upload.constants';
import logger from '@/logger';
import { downloadBlob } from '@/csv/utils/csv-generate-download-blob';
import { arrayToCsv } from '@/csv/utils/csv-array-to-csv';

const log = logger.getChild('CsvUpload');
log.debug('Mounting');
onMounted(() => {
  log.debug('Mounted');
});

const { $ta } = useI18N();

enum CsvUploadClientError {
  REQUIRED = 'REQUIRED',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  PARSING_ERROR = 'PARSING_ERROR',
  DUPLICATE_HEADERS = 'DUPLICATE_HEADERS',
  UNKNOWN = 'UNKNOWN',
}

type CsvUploadError =
  | CsvUploadClientError
  | CsvPostAssertErrorResponseTypeEnum
  | null;

const model = defineModel<File | null>({
  default: null,
});

const props = defineProps<{
  required?: boolean;
  apiError?: CsvPostAssertErrorResponseTypeEnum | null;
}>();

const validationTrigger = inject<Ref<number>>(TRIGGER_FORM_VALIDATION, ref(0));

watch([validationTrigger], () => {
  log.debug('Validation trigger');
  if (props.required && !file.value) {
    log.debug('Setting required error');
    setError(CsvUploadClientError.REQUIRED);
  }
});

const emit = defineEmits({
  'update:examples': (examples: string[]) => true,
  'update:error': (error: CsvUploadError) => true,
});

const isDragging = ref(false);
const file = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const error = ref<CsvUploadError>(null);
const headers = ref<string[]>([]);

const setError = (newError: CsvUploadError) => {
  log.debug('Setting error:', newError);
  const errorMessages = $ta('CSV_UPLOAD_ERROR');
  if (newError === null) {
    error.value = null;
    return;
  }

  if (!errorMessages[newError]) {
    log.error('Unknown error:', newError);
    error.value = CsvUploadClientError.UNKNOWN;
    return;
  }

  error.value = newError;
};

defineExpose({ setError });

watch(error, (newError) => {
  log.debug('Emitting error:', newError);
  emit('update:error', newError);
});

const setFile = (newFile: File | null) => {
  log.debug('Setting file');
  file.value = newFile;
  model.value = newFile;
};

const setData = (data: [string[], string[]]) => {
  log.debug('Setting data');
  headers.value = data[0];
  emit('update:examples', data[1]);
};

const clearFile = () => {
  log.debug('Clearing file');
  setFile(null);
  setData([[], []]);
  setError(null);
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const handleFile = async (inputFile?: File) => {
  if (!inputFile) return;

  log.debug('Handling file');

  file.value = null;
  if (validateFile(inputFile)) {
    try {
      setData(await validateHeaders(inputFile));
      setError(null);
      setFile(inputFile);
      log.log('File uploaded:', inputFile.name);
    } catch {
      setError(CsvUploadClientError.PARSING_ERROR);
      log.log('Error parsing file:', inputFile.name);
    }
  } else {
    setError(CsvUploadClientError.INVALID_FILE_TYPE);
  }
};

watch(
  () => model.value,
  async (file) => {
    log.debug('Model update:', file);
    if (file) {
      await handleFile(file);
    } else {
      clearFile();
    }
  },
  { immediate: true },
);

const handleDragOver = () => {
  log.debug('File dragged over');
  isDragging.value = true;
};

const handleDragLeave = () => {
  log.debug('File dragged out');
  isDragging.value = false;
};

const handleDrop = async (event: DragEvent) => {
  log.debug('File dropped');
  isDragging.value = false;

  const droppedFile = event.dataTransfer?.files[0];
  if (!droppedFile) {
    return;
  }
  await handleFile(droppedFile);
};

const onFile = async (event: Event) => {
  log.debug('File selected');
  const target = event.target as HTMLInputElement;
  const selectedFile = target.files?.[0];
  await handleFile(selectedFile);
};

const containerClasses = computed(() => {
  return {
    'd-bc-default': !error.value && !isDragging.value,
    'd-bc-critical': error.value && !isDragging.value,
    'd-bc-focus': isDragging.value,
    'd-bgc-info-subtle': isDragging.value,
    'd-bas-dashed d-bc-default': !file.value && !error.value,
  };
});

const openFileDialog = () => {
  log.debug('Opening file dialog');
  fileInput.value?.click();
};

const csv = arrayToCsv(TEMPLATE_DATA);
</script>
<template>
  <div class="csv-upload" :data-qa="DATA_QA.ROOT">
    <DtItemLayout
      :class="containerClasses"
      class="d-d-flex d-pl16 d-pr24 d-py16 d-ba d-bar16 d-baw1 d-hmn64 d-g16"
      :data-qa="DATA_QA.CONTAINER"
      @dragover.prevent="handleDragOver"
      @dragleave="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <template #left>
        <div class="d-d-flex d-ai-center d-jc-center d-h64 d-w64">
          <template v-if="file">
            <div
              class="d-d-flex d-ai-center d-jc-center d-h64 d-w64 d-bgc-secondary d-bar8"
            >
              <DtIcon name="google-sheets" size="500" />
            </div>
          </template>
          <template v-else>
            <DtIllustration name="file-upload" />
          </template>
        </div>
      </template>
      <label class="d-d-none">
        {{ $t('CSV_UPLOAD_INPUT_LABEL') }}
        <input
          ref="fileInput"
          class="d-vi-hidden"
          type="file"
          accept=".csv"
          :data-qa="DATA_QA.FILE_INPUT"
          @change="onFile"
        />
      </label>
      <div>
        <p
          v-if="file"
          :data-qa="DATA_QA.FILE_NAME"
          class="file-name d-ww-break-word"
        >
          {{ file.name }}
        </p>
        <i18n v-else path="CSV_UPLOAD_LABEL" tag="p" class="d-mb4">
          <template #uploadButton="{ upload, ariaLabel }">
            <DtLink
              href="#"
              :aria-label="ariaLabel"
              class=""
              :data-qa="DATA_QA.UPLOAD_LINK"
              @click.prevent="openFileDialog"
            >
              {{ upload }}
            </DtLink>
          </template>
        </i18n>
      </div>
      <template v-if="!file" #subtitle>
        {{ $t('CSV_UPLOAD_SUBTITLE') }}
      </template>
      <template v-if="file" #bottom>
        <div :data-qa="DATA_QA.HEADERS">
          <DtBadge v-for="header in headers" :key="header" class="d-mr4 d-my2">
            {{ header }}
          </DtBadge>
        </div>
      </template>
      <template v-if="file" #right>
        <DtTooltip placement="top">
          {{ $t('CSV_UPLOAD_NEW_FILE_TOOLTIP') }}
          <template #anchor>
            <DtButton
              importance="clear"
              kind="muted"
              class="d-mr2"
              :data-qa="DATA_QA.UPLOAD_ANOTHER"
              @click="openFileDialog"
            >
              <template #icon>
                <DtIcon name="upload" size="400" />
              </template>
            </DtButton>
          </template>
        </DtTooltip>
        <DtTooltip placement="top">
          {{ $t('CSV_UPLOAD_REMOVE_TOOLTIP') }}
          <template #anchor>
            <DtButton
              importance="clear"
              kind="muted"
              :data-qa="DATA_QA.CLEAR"
              @click="clearFile"
            >
              <template #icon>
                <DtIcon name="trash" size="400" />
              </template>
            </DtButton>
          </template>
        </DtTooltip>
      </template>
    </DtItemLayout>
    <DtValidationMessages
      v-if="error"
      :data-qa="DATA_QA.ERROR_MESSAGE"
      :validation-messages="[
        { message: $ta(`CSV_UPLOAD_ERROR`)[error], type: 'error' },
      ]"
    />
    <p class="d-mt16 d-mb4 d-fs-100 d-fc-tertiary">
      {{ $t('CSV_UPLOAD_TITLE') }}
    </p>
    <ul class="d-ml16 d-fs-100 d-fc-tertiary">
      <li class="d-lst-disc">{{ $t('CSV_UPLOAD_RULE_1') }}</li>
      <li class="d-lst-disc">{{ $t('CSV_UPLOAD_RULE_2') }}</li>
      <li class="d-lst-disc">{{ $t('CSV_UPLOAD_RULE_3') }}</li>
      <li class="d-lst-disc">
        <DtLink
          href="#"
          :data-qa="DATA_QA.DOWNLOAD_TEMPLATE"
          @click="downloadBlob(csv, 'template.csv', 'text/csv;charset=utf-8;')"
        >
          {{ $t('CSV_UPLOAD_DOWNLOAD_TEMPLATE') }}
        </DtLink>
      </li>
    </ul>
  </div>
</template>
<style type="less">
.csv-upload .d-item-layout__left {
  min-width: var(--dt-size-700) !important;
  width: var(--dt-size-700) !important;
  height: var(--dt-size-700) !important;
  padding-right: var(--dt-space-0) !important;
  justify-content: center !important;
  align-self: center !important;
}
</style>
